/**
 * Sandbox attribute shared by every email preview iframe.
 *
 * `allow-same-origin` lets the parent read `contentWindow.document` for the
 * auto-height sizer. `allow-popups` and `allow-popups-to-escape-sandbox` make a
 * `target="_blank"` link open as a normal document instead of being inert.
 *
 * SECURITY: `allow-scripts` is omitted deliberately — combined with
 * `allow-same-origin` it would void the sandbox for untrusted email HTML.
 * `allow-top-navigation` is omitted so no preview can navigate the application
 * away from the page the user is working on.
 */
export const EMAIL_PREVIEW_SANDBOX =
  "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const REL_TOKENS = ["noopener", "noreferrer"];

/**
 * Rewrite every anchor in email HTML so it opens in a new tab.
 *
 * Anchors with a missing, empty or fragment-only `href` are left untouched, so a
 * broken anchor does nothing and an in-document fragment still scrolls within
 * the preview.
 *
 * @param {string} html Email HTML document or fragment.
 * @returns {string} The rewritten HTML, or the input unchanged when falsy.
 */
export const rewriteEmailPreviewLinks = html => {
  if (!html) return html;

  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.querySelectorAll("a[href]").forEach(anchor => {
    const href = anchor.getAttribute("href").trim();
    if (!href || href.startsWith("#")) return;

    anchor.setAttribute("target", "_blank");

    const rel = new Set(
      (anchor.getAttribute("rel") || "").split(/\s+/).filter(Boolean)
    );
    REL_TOKENS.forEach(token => rel.add(token));
    anchor.setAttribute("rel", [...rel].join(" "));
  });

  const doctype = doc.doctype
    ? new XMLSerializer().serializeToString(doc.doctype)
    : "";
  return doctype + doc.documentElement.outerHTML;
};
